import { useEffect, useMemo, useState } from "react";

import { SettingsField } from "@/components/v2/settings-field";
import { FormSheetFooter } from "@/components/v2/form-sheet-footer";
import { DangerConfirmDialog } from "@/components/danger-zone";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAccountUpdateUserRole } from "@/hooks/use-account-users";
import type { AccountUserOnAccount } from "@adva/types";
import { ACCOUNT_ROLE_OPTIONS } from "@/lib/role-options";

// ADV-881: Extracted from /settings/members so the member detail page can
// reuse the same sheet. ADV-1034: now consumes the shared
// ACCOUNT_ROLE_OPTIONS — "owner" was removed from the picker because the
// API rejects it (AccountRoleSchema excludes it since ADV-1047).

interface EditRoleSheetProps {
  member: AccountUserOnAccount | null;
  onClose: () => void;
}

const END_USER_ROLES = [
  { value: "manager", label: "Manager" },
  { value: "salesperson", label: "Salesperson" },
  { value: "crew_chief", label: "Crew Chief" },
] as const;

export function EditRoleSheet({ member, onClose }: EditRoleSheetProps) {
  const [role, setRole] = useState<string>(member?.account_role ?? "admin");
  const [endUserRole, setEndUserRole] = useState<string>(
    member?.businesses[0]?.end_user_role ?? "manager",
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const updateRole = useAccountUpdateUserRole();

  useEffect(() => {
    if (member) {
      setRole(member.account_role);
      setEndUserRole(member.businesses[0]?.end_user_role ?? "manager");
      setError(null);
    }
  }, [member]);

  const previousRole = member?.account_role;
  const selectedOption = useMemo(
    () => ACCOUNT_ROLE_OPTIONS.find((r) => r.value === role),
    [role],
  );

  // Escalation = promoting INTO a privileged role from a non-privileged one,
  // OR moving between two privileged roles (owner ↔ admin). Demotions and
  // moves between non-privileged roles run inline.
  const isEscalation = useMemo(() => {
    if (!previousRole || previousRole === role) return false;
    return selectedOption?.isPrivileged ?? false;
  }, [previousRole, role, selectedOption]);

  function handleClose() {
    setError(null);
    setConfirmOpen(false);
    onClose();
  }

  async function applyUpdate() {
    if (!member) return;
    setError(null);
    setSubmitting(true);
    try {
      const payload: Parameters<typeof updateRole.mutateAsync>[0] = {
        userId: member.id,
        account_role: role,
      };
      if (role === "viewer" && member.businesses.length > 0) {
        payload.business_assignments = member.businesses.map((b) => ({
          business_id: b.business_id,
          role: b.role ?? "viewer",
          end_user_role: endUserRole,
        }));
      }
      await updateRole.mutateAsync(payload);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit() {
    if (!member) return;
    if (isEscalation) {
      setConfirmOpen(true);
      return;
    }
    await applyUpdate();
  }

  const name = member
    ? [member.first_name, member.last_name].filter(Boolean).join(" ") ||
      member.email
    : "";

  const confirmSlug = `promote to ${selectedOption?.label.toLowerCase() ?? role}`;

  return (
    <>
      <Sheet
        open={!!member}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
      >
        <SheetContent className="w-[480px] sm:max-w-[480px] flex flex-col">
          <SheetHeader>
            <SheetTitle>Edit Member</SheetTitle>
          </SheetHeader>
          <form
            id="edit-role-form"
            className="flex-1 py-4 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            <p className="typo-body text-[color:var(--color-text-md)]">
              {name}
            </p>
            <SettingsField label="Role">
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ACCOUNT_ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      <div className="flex flex-col py-0.5">
                        <span className="typo-body text-[color:var(--color-text-hi)]">
                          {r.label}
                        </span>
                        <span className="typo-label text-[color:var(--color-text-lo)]">
                          {r.description}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedOption && (
                <p className="mt-2 typo-label text-[color:var(--color-text-md)]">
                  {selectedOption.description}
                </p>
              )}
            </SettingsField>
            {role === "viewer" && (
              <SettingsField label="Functional Role">
                <Select value={endUserRole} onValueChange={setEndUserRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {END_USER_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-2 typo-label text-[color:var(--color-text-md)]">
                  Defines which data this user can access within each business.
                </p>
              </SettingsField>
            )}
            {error && <p className="typo-body text-destructive">{error}</p>}
          </form>
          <FormSheetFooter
            onCancel={handleClose}
            isSubmitting={submitting}
            formId="edit-role-form"
          />
        </SheetContent>
      </Sheet>

      <DangerConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Promote ${name || "user"} to ${selectedOption?.label ?? "this role"}?`}
        description={
          <span>
            <strong>{selectedOption?.label}</strong> has broad access:{" "}
            {selectedOption?.description ?? ""} This change is recorded in the
            account&apos;s audit log and the user will see the new permissions
            on their next sign-in.
          </span>
        }
        confirmSlug={confirmSlug}
        actionLabel="Promote"
        pendingLabel="Promoting…"
        auditTrailText="A role-change entry will be written to the activity log."
        isPending={submitting}
        onConfirm={async () => {
          await applyUpdate();
        }}
      />
    </>
  );
}
